/* Batch 34 · B34-9 / B34-10 — next to "Support ref" on the vouching sheet:
   - the "typed by user" mark on a reference typed over the linked documents' reference (the server computes the reference
     from the documents on read: support_ref_source "document" | "typed");
   - "Amount per support document" (net of VAT; VAT lines → VAT; AR / AP / bank lines → gross — the basis shown per cell), an
     override marked "typed by user", "not read" when the extraction has no amount;
   - "Difference" (row − document), flagged when not nil, stronger colour at or above clearly trivial. */
import React, { useEffect, useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { fmtNumber } from "@/lib/format";
import { toast } from "sonner";
import { Info } from "lucide-react";
import { ResizableTh } from "@/components/ui/resizable-table";   // Batch 35 · B35-8

const fmt = (v, lang) => fmtNumber(v, lang, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function SupportRefTypedMark({ item, t }) {
  if (item?.support_ref_source === "document_name") {     // S20: no extracted reference → "DOC-ref · file name" (automatic)
    return (
      <span className="ms-1 rounded bg-slate-100 px-1 text-[9px] text-slate-600 dark:bg-slate-800 dark:text-slate-300"
            title={t("b34m.refFromNameTitle")} data-testid={`vouch-ref-docname-${item.id}`}>{t("b34m.refFromName")}</span>
    );
  }
  if (item?.support_ref_source !== "typed") return null;
  return (
    <span className="ms-1 rounded bg-amber-100 px-1 text-[9px] text-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
          title={t("b34a.refTypedTitle")} data-testid={`vouch-ref-typed-${item.id}`}>{t("b34a.refTyped")}</span>
  );
}

/* Batch 35 · B35-8: the long note of "Amount per support document" sits in a tooltip on an (i) icon (kept for screen readers);
   with ``onResizeStart`` + ``indices`` (the vouching sheet) both headers are resizable columns. */
export function SupportAmountHeaders({ t, onResizeStart, indices, thClass = "px-2 py-2" }) {
  const H = ({ k, className, children, ...rest }) => (onResizeStart && indices
    ? <ResizableTh index={indices[k]} onResizeStart={onResizeStart} className={`${thClass} ${className}`} {...rest}>{children}</ResizableTh>
    : <th className={`${thClass} ${className}`} {...rest}>{children}</th>);
  const hint = t("b34a.docAmountHint");
  return (
    <>
      {H({ k: "docAmount", className: "text-end whitespace-normal", "data-testid": "vouch-hdr-doc-amount", children: (
        <span className="inline-flex items-start justify-end gap-1">
          <span>{t("b34a.docAmount")}</span>
          <button type="button" title={hint} aria-label={t("b35v.aboutColumn")}
                  className="mt-px shrink-0 rounded-full text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-sky-400"
                  data-testid="vouch-hdr-doc-amount-info"><Info className="h-3.5 w-3.5" aria-hidden="true" /></button>
          <span className="sr-only">{hint}</span>
        </span>) })}
      {H({ k: "diff", className: "text-end whitespace-normal", title: t("b34a.differenceHint"), "data-testid": "vouch-hdr-doc-diff",
           children: t("b34a.difference") })}
    </>
  );
}

export function SupportAmountCells({ item, t, lang, onChanged, readOnly }) {
  const [v, setV] = useState(item.support_doc_amount_override ?? "");
  useEffect(() => { setV(item.support_doc_amount_override ?? ""); }, [item.support_doc_amount_override]);
  const src = item.support_doc_amount_source || "";
  const save = async () => {
    const cur = item.support_doc_amount_override ?? "";
    if (String(v) === String(cur)) return;
    const amount = String(v).trim() === "" ? null : Number(String(v).replace(/,/g, ""));
    if (amount !== null && Number.isNaN(amount)) { setV(cur); return; }
    try {
      await api.put(`/vouching/${item.id}/support-amount`, { amount });
      toast.success(t("b34a.amountSaved"));
      onChanged && onChanged();
    } catch (e) { toast.error(toErrMsg(e, t("b34a.saveFailed"))); setV(cur); }
  };
  const flag = item.support_doc_difference_flag;
  // A-33: "unrated" = clearly trivial not set in 105(a) — neutral, with the note; never shown as a minor difference
  const diffCls = flag === "strong" ? "bg-red-100 font-semibold text-red-800 dark:bg-red-950/50 dark:text-red-200"
    : flag === "minor" ? "bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
    : flag === "unrated" ? "bg-slate-50 text-slate-700 dark:bg-slate-900/40 dark:text-slate-300" : "";
  const hasDocs = (item.document_ids || []).length > 0;
  return (
    <>
      {/* Batch 35 · B35-8: line 1 = basis tag (+ typed mark) and the amount, right-aligned like the Amount column; line 2 = the
          override box */}
      <td className="px-2 py-2 text-end text-xs align-top" data-testid={`vouch-doc-amount-${item.id}`}>
        <div dir="ltr" className="flex flex-wrap items-baseline justify-end gap-x-1" data-testid={`vouch-doc-amount-line1-${item.id}`}>
          {item.support_doc_amount != null ? (<>
            {src === "typed" && <span className="rounded bg-amber-100 px-1 text-[9px] text-amber-800" data-testid={`vouch-doc-amount-typed-${item.id}`}>{t("b34a.amountTyped")}</span>}
            <span className="text-[9px] text-muted-foreground" data-testid={`vouch-doc-amount-basis-${item.id}`}>{t(`b34a.basis_${item.support_doc_amount_basis || "net"}`)}</span>
            <span className="num-cell whitespace-nowrap" data-testid={`vouch-doc-amount-value-${item.id}`}>{fmt(item.support_doc_amount, lang)}</span>
          </>) : src === "not_read" ? <span className="italic text-muted-foreground" data-testid={`vouch-doc-amount-notread-${item.id}`}>{t("b34a.notRead")}</span>
            : <span className="text-muted-foreground">—</span>}
        </div>
        {!readOnly && hasDocs && (
          <input value={v} onChange={(e) => setV(e.target.value)} onBlur={save} onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                 inputMode="decimal" title={t("b34a.amountOverride")} placeholder={t("b34a.amountOverride")} dir="ltr"
                 className="mt-1 block w-full rounded border px-1 py-0.5 text-end text-[10px] dark:bg-slate-900" data-testid={`vouch-doc-amount-input-${item.id}`} />
        )}
      </td>
      <td className={`px-2 py-2 num-cell text-xs whitespace-nowrap align-top ${diffCls}`} data-flag={flag || ""} data-testid={`vouch-doc-diff-${item.id}`}
          title={flag === "unrated" ? t("sd.differenceUnrated") : undefined}>
        {item.support_doc_difference != null ? fmt(item.support_doc_difference, lang) : "—"}
        {flag === "unrated" && (
          <div className="whitespace-normal text-[9px] font-normal text-muted-foreground" data-testid={`vouch-doc-diff-note-${item.id}`}>
            {t("sd.differenceUnrated")}
          </div>
        )}
        {item.support_doc_level === "invoice" && (   /* Step 11c · C3c · find 18: compared at invoice level */
          <div className="whitespace-normal text-[9px] font-normal text-muted-foreground" data-testid={`vouch-doc-diff-level-${item.id}`}>
            {t("s11c3c.vouching.invoiceLevel").replace("{n}", item.support_doc_group_lines)}
          </div>
        )}
      </td>
    </>
  );
}
